import { createFileRoute } from '@tanstack/react-router';
import { useTranslate } from '@tolgee/react';
import { AppRoute } from '@agentselly/immodossier-shared';
import { useLoaderQuery } from '~/helper/useLoaderQuery';
import { useAppParams } from '~/helper/useAppParams';
import { LoadingWidget } from '~/components/widget/base/LoadingWidget';
import { ErrorWidget } from '~/components/widget/base/ErrorWidget';
import { AppLink } from '~/components/AppLink';
import { useAuthz } from '~/context/AuthzContext';
import { RenderWidgets } from '~/components/widget/RenderWidgets';
import { cockpitDashboardWidgets } from '~/config/widgets/cockpit/cockpitDashboardWidgets';
import {
  Avatar,
  CardLink,
  fmtDate,
  Ico,
  Kpi,
  PageHead,
  V3Page,
  type IconName,
} from '~/components/cockpit/v3';
import {
  flattenActivities,
  type CockpitEvent,
  type WidgetProps,
} from '~/components/cockpit/cockpitData';
import { getCockpitActivityTitle } from '~/components/widget/cockpit/cockpitActivityDesignDataConfig';
import { useEffect, useRef } from 'react';
import { trackEvent } from '@agentselly/analytics';

export const Route = createFileRoute(
  AppRoute.$LANG_$COMPANYID_$CONTACTID_COCKPIT_$PROPERTYID_HOME,
)({
  component: HomePage,
});

interface Contact {
  readonly firstname?: string;
  readonly lastname?: string;
}

interface SalesDoc {
  readonly type?: string;
  readonly url?: string;
}

const fullName = (c?: Contact): string =>
  [
    c?.firstname,
    c?.lastname,
  ]
    .filter(Boolean)
    .join(' ');

const ACTIVITY_ICONS: Record<string, IconName> = {
  mail: 'mail',
  phone: 'phone',
  visit: 'cal',
  offer: 'coin',
  download: 'doc',
};

const activityIcon = (type: string): IconName =>
  ACTIVITY_ICONS[
    Object.keys(ACTIVITY_ICONS).find((k) => type.toLowerCase().includes(k)) ??
      ''
  ] ?? 'info';

const when = (value: string): string => {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '–';
  return `${d.toLocaleDateString('de-CH', {
    weekday: 'short',
    day: '2-digit',
    month: '2-digit',
  })} ${d.toLocaleTimeString('de-CH', { hour: '2-digit', minute: '2-digit' })}`;
};

function PersonCard(props: {
  readonly odId: string;
  readonly title: string;
  readonly name: string;
  readonly sub: string;
  readonly labelled?: boolean;
}): React.ReactElement {
  return (
    <section
      className="card stack"
      style={{ gap: 12 }}
      data-od-id={props.odId}
    >
      <h2>{props.title}</h2>
      <div className="pers">
        <Avatar
          name={props.name}
          labelled={props.labelled}
        />
        <span style={{ minWidth: 0 }}>
          <span
            className="t"
            style={{ display: 'block' }}
          >
            {props.name}
          </span>
          <span className="sm mu">{props.sub}</span>
        </span>
      </div>
    </section>
  );
}

function HomePage(): React.ReactElement {
  const params = useAppParams(Route);
  const { t } = useTranslate();
  const { atLeast } = useAuthz();
  const isAdvisor = atLeast('partner');

  const trackedRef = useRef(false);
  useEffect(() => {
    if (trackedRef.current) return;
    trackedRef.current = true;
    trackEvent('immodossier cockpit opened');
  }, []);

  const query = useLoaderQuery(
    AppRoute.$LANG_$COMPANYID_$CONTACTID_COCKPIT_$PROPERTYID_HOME,
    { params },
  );

  if (query.isLoading) return <LoadingWidget />;
  if (query.isError || !query.data) return <ErrorWidget error={query.error} />;

  if (!query.data.v3Redesign) {
    const widgetProps = query.data?.widgetProps || {};
    const widgets = cockpitDashboardWidgets(widgetProps);
    return (
      <div className="h-full flex flex-col gap-y-6">
        <RenderWidgets widgets={widgets} />
      </div>
    );
  }

  const wp = (query.data.widgetProps || {}) as WidgetProps;
  const welcome = wp.cockpitWelcome ?? {};
  const property = welcome.property ?? {};
  const expose = wp.cockpitExpose ?? {};
  const photos: string[] = (expose.photos ?? []).slice(0, 4);
  const docs: SalesDoc[] = expose.documents ?? [];
  const visits: CockpitEvent[] = (wp.cockpitCalendarEventsWidget?.booked ?? [])
    .filter((e: CockpitEvent) => new Date(e.date).getTime() >= Date.now())
    .slice(0, 3);
  const activities = flattenActivities(wp.cockpitActivities?.activities).slice(
    0,
    5,
  );
  const owner = fullName(welcome.person) || '–';
  const consultant = fullName(welcome.consultant) || 'AgentSelly';
  const propertyStatus: string | undefined = property.status ?? expose.status;
  const cell = (key: string): { value: string | number; delta?: number } => ({
    value: wp[key]?.value ?? '–',
    delta: wp[key]?.changeInLastWeek,
  });
  const go = (
    <span
      className="end"
      aria-hidden="true"
    >
      <Ico
        name="arrow"
        size={16}
      />
    </span>
  );
  const tile = (
    key: string,
    id: string,
    label: string,
    to: string,
  ): React.ReactElement => {
    const c = cell(key);
    return (
      <AppLink
        to={to}
        params={params}
        className="card kpi"
        data-od-id={id}
        data-widget={key}
      >
        <div className="n">{c.value}</div>
        <div className="l">{label}</div>
        {c.delta ? (
          <div className={`d ${c.delta > 0 ? 'up' : 'down'}`}>
            {c.delta > 0 ? '+' : '−'}
            {Math.abs(c.delta)} {t('cockpit_v3_vs_week', 'ggü. Vorwoche')}
          </div>
        ) : null}
        <span className="go">
          {t('cockpit_home_details', 'Details')}
          <Ico
            name="arrow"
            size={16}
          />
        </span>
      </AppLink>
    );
  };

  return (
    <V3Page>
      <div data-widget="cockpitWelcome">
        <PageHead
          odId="cockpit-head"
          title={t('cockpit_home_title', 'Dashboard')}
          badges={
            <>
              {propertyStatus ? (
                <span className="chip c-ok">
                  <i />
                  {propertyStatus}
                </span>
              ) : null}
              {welcome.lead?.status ? (
                <span className="chip c-neu">
                  <i />
                  {t('cockpit_home_lead', 'Lead')}: {welcome.lead.status}
                </span>
              ) : null}
            </>
          }
          sub={t('cockpit_home_sub', 'Vermarktung im Überblick')}
        />
        {isAdvisor || !welcome.person?.firstname ? null : (
          <p
            className="sm mu"
            style={{ marginTop: 4 }}
          >
            {t('cockpit_home_greeting', 'Guten Tag')},{' '}
            {welcome.person.firstname}{' '}
            {t(
              'cockpit_home_greeting_sub',
              '– hier sehen Sie, wie die Vermarktung Ihrer Immobilie läuft.',
            )}
          </p>
        )}
      </div>

      <div
        className="grid g4"
        data-od-id="kpi-row"
      >
        <Kpi
          odId="kpi-ad-views"
          data-widget="cockpitAdViews"
          value={cell('cockpitAdViews').value}
          delta={cell('cockpitAdViews').delta}
          label={t('cockpit_home_kpi_views', 'Inserat-Aufrufe')}
        />
        <Kpi
          odId="kpi-digital-tour"
          data-widget="cockpitDigitalTour"
          value={cell('cockpitDigitalTour').value}
          delta={cell('cockpitDigitalTour').delta}
          label={t('cockpit_home_kpi_tour', 'Digitaler Rundgang')}
        />
        <Kpi
          odId="kpi-expose-downloads"
          data-widget="cockpitExposeDownloads"
          value={cell('cockpitExposeDownloads').value}
          delta={cell('cockpitExposeDownloads').delta}
          label={t('cockpit_home_kpi_downloads', 'Exposé-Downloads')}
        />
        <Kpi
          odId="kpi-booked-visits"
          data-widget="cockpitBookedVisits"
          value={cell('cockpitBookedVisits').value}
          label={t('cockpit_home_kpi_visits', 'Gebuchte Besichtigungen')}
          note={t('cockpit_home_upcoming', 'bevorstehend')}
        />
      </div>

      <div className="grid split g2">
        <div className="stack">
          <section
            className="card"
            data-od-id="calendar-events"
            data-widget="cockpitCalendarEventsWidget"
          >
            <div className="sec">
              <h2>{t('cockpit_home_next_visits', 'Nächste Besichtigungen')}</h2>
              <CardLink
                to={wp.cockpitCalendarEventsWidget?.link}
                odId="link-calendar"
              >
                {t('cockpit_home_calendar', 'Kalender')}
              </CardLink>
            </div>
            {visits.length === 0 ? (
              <p
                className="sm mu"
                style={{ padding: '8px 0' }}
              >
                {t(
                  'cockpit_home_no_visits',
                  'Noch keine Besichtigung gebucht. Termine erscheinen hier, sobald Interessenten einen Slot buchen.',
                )}
              </p>
            ) : (
              visits.map((e, i) => (
                <div
                  className="item"
                  key={`${e.date}-${i}`}
                  data-od-id={`event-${i}`}
                >
                  <span className="ico">
                    <Ico
                      name="cal"
                      size={18}
                    />
                  </span>
                  <span style={{ minWidth: 0 }}>
                    <span
                      className="t"
                      style={{ display: 'block' }}
                    >
                      {t('cockpit_home_visit', 'Besichtigung')} · {when(e.date)}{' '}
                      {t('cockpit_home_oclock', 'Uhr')}
                    </span>
                    <span
                      className="sm mu"
                      style={{ display: 'block' }}
                    >
                      {e.bookedBy
                        ? `${t('cockpit_home_booked_by', 'Gebucht von')} ${
                            e.bookedBy
                          }`
                        : ''}
                      {e.hostedBy
                        ? ` · ${t('cockpit_home_host', 'Gastgeber')} ${
                            e.hostedBy
                          }`
                        : ''}
                    </span>
                  </span>
                </div>
              ))
            )}
          </section>

          <div
            className="grid g2"
            data-od-id="pipeline"
          >
            {tile(
              'cockpitInterestedBuyers',
              'tile-interested',
              t('cockpit_home_tile_interested', 'Aktuell interessiert'),
              AppRoute.$LANG_$COMPANYID_$CONTACTID_COCKPIT_$PROPERTYID_INTEREST,
            )}
            {tile(
              'cockpitOffers',
              'tile-offers',
              t('cockpit_home_tile_offers', 'Angebote'),
              AppRoute.$LANG_$COMPANYID_$CONTACTID_COCKPIT_$PROPERTYID_OFFERS,
            )}
          </div>

          <section
            className="card"
            data-od-id="activities"
            data-widget="cockpitActivities"
          >
            <div className="sec">
              <h2>{t('cockpit_home_activities', 'Aktivitäten')}</h2>
              <CardLink
                to={wp.cockpitActivities?.link}
                odId="link-activities"
              >
                {t('cockpit_home_all', 'Alle')}
              </CardLink>
            </div>
            {activities.length === 0 ? (
              <p
                className="sm mu"
                style={{ padding: '8px 0' }}
              >
                {t(
                  'cockpit_home_no_activities',
                  'Noch keine Aktivitäten erfasst.',
                )}
              </p>
            ) : (
              activities.map((a, i) => (
                <div
                  className="item"
                  key={`${a.date}-${i}`}
                  data-od-id={`activity-${i}`}
                >
                  <span className="ico">
                    <Ico
                      name={activityIcon(a.type)}
                      size={18}
                    />
                  </span>
                  <span style={{ minWidth: 0 }}>
                    <span
                      className="t"
                      style={{ display: 'block' }}
                    >
                      {getCockpitActivityTitle(t, a.type)}
                    </span>
                    <span className="sm mu">
                      {a.person ? `${a.person} · ` : ''}
                      {fmtDate(a.date)}
                    </span>
                  </span>
                </div>
              ))
            )}
          </section>
        </div>

        <div className="stack">
          {isAdvisor ? (
            <PersonCard
              odId="welcome-owner"
              title={t('cockpit_home_owner', 'Eigentümer')}
              name={owner}
              sub={t('cockpit_home_care_you', 'Betreuung: Sie')}
            />
          ) : (
            <PersonCard
              odId="welcome-consultant"
              title={t('cockpit_home_advisor', 'Ihr Berater')}
              name={consultant}
              sub="AgentSelly"
              labelled
            />
          )}

          <section
            className="card stack"
            style={{ gap: 12 }}
            data-od-id="expose"
            data-widget="cockpitExpose"
          >
            <div className="sec">
              <h2>{t('cockpit_home_sales_docs', 'Verkaufsdokumentation')}</h2>
              {expose.status ? (
                <span className="chip c-ok">
                  <i />
                  {expose.status}
                </span>
              ) : (
                <span className="chip c-neu">
                  <i />
                  {t('cockpit_home_in_prep', 'In Vorbereitung')}
                </span>
              )}
            </div>
            <div
              className="sd"
              data-od-id="sales-docs"
            >
              {(
                [
                  [
                    'expose',
                    t('cockpit_home_doc_expose', 'Exposé'),
                  ],
                  [
                    'flyer',
                    t('cockpit_home_doc_flyer', 'Flyer'),
                  ],
                ] as const
              ).map(
                ([
                  type,
                  label,
                ]) => {
                  const url = docs.find((d) => d.type === type)?.url;
                  const body = (
                    <>
                      <span className="ico">
                        <Ico
                          name="doc"
                          size={18}
                        />
                      </span>
                      <span className="bd">
                        <span className="t">{label}</span>
                        <span className="sm mu">
                          {url
                            ? t('cockpit_home_pdf_open', 'PDF · öffnen')
                            : t(
                                'cockpit_home_not_uploaded',
                                'Noch nicht hochgeladen',
                              )}
                        </span>
                      </span>
                    </>
                  );
                  return url ? (
                    <a
                      key={type}
                      className="item"
                      href={url}
                      data-od-id={`sales-doc-${type}`}
                      aria-label={`${label} ${t(
                        'cockpit_home_open_pdf',
                        'öffnen (PDF)',
                      )}`}
                    >
                      {body}
                      {go}
                    </a>
                  ) : (
                    <div
                      key={type}
                      className="item"
                      data-od-id={`sales-doc-${type}`}
                    >
                      {body}
                    </div>
                  );
                },
              )}
              <div
                className="item gi"
                data-od-id="sales-doc-gallery"
              >
                <span className="ico">
                  <Ico
                    name="house"
                    size={18}
                  />
                </span>
                <span className="bd">
                  <span className="t">
                    {t('cockpit_home_gallery', 'Bildergalerie')}
                  </span>
                  <span className="sm mu">
                    {photos.length
                      ? `${photos.length} ${t('cockpit_home_photos', 'Fotos')}`
                      : t('cockpit_home_no_photos', 'Noch keine Fotos')}
                  </span>
                </span>
              </div>
              {photos.length ? (
                <div
                  className="gal"
                  data-od-id="gallery"
                >
                  {photos.map((p, i) => (
                    <img
                      key={p}
                      src={p}
                      width={900}
                      height={600}
                      loading="lazy"
                      alt={`${t('cockpit_home_photo', 'Galeriefoto')} ${
                        i + 1
                      } ${t('cockpit_home_of', 'von')} ${photos.length} – ${
                        property.title ?? ''
                      }`}
                    />
                  ))}
                </div>
              ) : (
                <div
                  className="pic-ph"
                  role="img"
                  aria-label={t(
                    'cockpit_home_no_photos_alt',
                    'Noch keine Fotos in der Bildergalerie',
                  )}
                >
                  <Ico
                    name="house"
                    size={28}
                  />
                </div>
              )}
              <AppLink
                to={
                  AppRoute.$LANG_$COMPANYID_$CONTACTID_COCKPIT_$PROPERTYID_DOCUMENTS
                }
                params={params}
                className="item"
                data-od-id="sales-doc-documents"
              >
                <span className="ico">
                  <Ico
                    name="folder"
                    size={18}
                  />
                </span>
                <span className="bd">
                  <span className="t">
                    {t('cockpit_home_documents', 'Dokumente')}
                  </span>
                  <span className="sm mu">
                    {t(
                      'cockpit_home_documents_sub',
                      'Verkaufsvertrag, Grundriss, Energieausweis u. a.',
                    )}
                  </span>
                </span>
                {go}
              </AppLink>
            </div>
          </section>
        </div>
      </div>
    </V3Page>
  );
}
